/**
 * Is there a stored token worth sending?
 *
 * Not the same question as `AuthService.loggedIn`, which is a BehaviorSubject
 * resolved asynchronously: the callers that need this run in a constructor or
 * ngOnInit, before it has emitted.
 *
 * ## Why this exists at all
 *
 * The nav headers render around every route, /login included, and polled
 * guarded endpoints without checking. auth/guard.js answers those
 * 403 {message:'missing token'}, and the interceptor turns that into a red
 * toast over the login form -- then another every 60 seconds while the page
 * sits open. Invisible on an SSO deployment, which redirects away from /login
 * before anyone is anonymous there long enough to notice.
 *
 * Shared rather than a private method on each header: there are two headers
 * with the identical defect, and a rule duplicated in two components is a rule
 * that gets fixed in one.
 */

/**
 * @param tokenKey the localStorage key, from SharedService.getAccessToken()
 *
 * The 'undefined' and 'null' string checks are not paranoia: an earlier code
 * path wrote those literals into localStorage, login.component.ts still tests
 * for 'undefined' because of it, and they are present in existing browsers.
 */
export function hasStoredSession(tokenKey: string): boolean {
    if (!tokenKey) {
        return false;
    }
    let token: string | null = null;
    try {
        token = localStorage.getItem(tokenKey);
    } catch {
        // Storage can throw outright rather than return null -- a browser set
        // to block site data, or a sandboxed context. Treat it as anonymous.
        return false;
    }
    return !!token && token !== 'undefined' && token !== 'null';
}
